// The account page's own sessions list (DESIGN.md §8.3: "Own profile, consents, sessions |
// read/write own", api-contract.yaml `auth_sessions_list` / `auth_sessions_revoke`). Every write
// here is a `DELETE`, so `revokeSession` — not this hook — is what carries the CSRF header
// (T-03's `lib/api/client.ts`); a 403 `csrf_token_invalid` surfaces as `mutation.error`, and the
// caller renders it with `CsrfRecoveryNotice` (DESIGN.md §8.1.1, this task).
'use client';

import { useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';
import { listSessions, revokeSession, type Session } from '../api';

export const SESSIONS_QUERY_KEY = ['auth', 'sessions'] as const;

async function fetchSessions(): Promise<Session[]> {
  const { data, error } = await listSessions();
  if (error) {
    throw error;
  }
  return data.data;
}

export function useSessions(): UseQueryResult<Session[]> {
  return useQuery({ queryKey: SESSIONS_QUERY_KEY, queryFn: fetchSessions });
}

/** `mutate(sessionId)`. On success the list is refetched so a revoked session disappears. */
export function useRevokeSession() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (sessionId: string) => {
      const { error } = await revokeSession(sessionId);
      if (error) {
        throw error;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: SESSIONS_QUERY_KEY });
    },
  });
}
